import React, { useState, useEffect } from 'react';
import { X, Package, Calendar, User, Building, DollarSign, FileText, Loader, ShoppingCart, Hash, AlertTriangle, Filter, Download } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import salesTeamService from '../services/salesTeamService';

const ProductSalesDetailModal = ({ 
  isOpen, 
  onClose, 
  salesmanCode, 
  salesmanName, 
  startDate, 
  endDate 
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [groupedProducts, setGroupedProducts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all'); // 'all', 'SAT', 'Solar', 'MTF', 'MTB', 'Other'

  useEffect(() => {
    if (isOpen && salesmanCode) {
      loadProductSalesDetail();
    }
  }, [isOpen, salesmanCode, startDate, endDate]);

  const loadProductSalesDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      setData(null);

      const response = await salesTeamService.getProductSalesDetail(
        salesmanCode,
        startDate,
        endDate
      );

      if (response.success) {
        setData(response.data);
        // Group products by factory type
        await groupProductsByType(response.data.products || []);
      } else {
        setError(response.error || 'ไม่สามารถดึงข้อมูลรายละเอียดการขายได้');
      }
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      console.error('Error loading product sales detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const groupProductsByType = async (products) => {
    const groups = {
      'SAT': [],
      'Solar': [],
      'MTF': [],
      'MTB': [],
      'Other': []
    };

    // Process products in batches to avoid too many concurrent requests
    const batchSize = 5;
    for (let i = 0; i < products.length; i += batchSize) {
      const batch = products.slice(i, i + batchSize);
      const batchPromises = batch.map(async (product) => {
        try {
          const factoryTypeResponse = await salesTeamService.getFactoryType(product.item_number);
          if (factoryTypeResponse.success && factoryTypeResponse.data) {
            const factoryType = factoryTypeResponse.data.factory_type;
            // Map factory types to our categories
            let category = 'Other';
            if (factoryType === 'SAT' || factoryType?.toLowerCase() === 'satellite') category = 'SAT';
            else if (factoryType === 'Solar' || factoryType?.toLowerCase() === 'solar') category = 'Solar';
            else if (factoryType === 'MTF') category = 'MTF';
            else if (factoryType === 'MTB') category = 'MTB';
            else if (factoryType?.toLowerCase() === 'mounting') category = 'MTF';
            
            return { product, category };
          } else {
            return { product, category: 'Other' };
          }
        } catch (error) {
          console.error(`Error getting factory type for ${product.item_number}:`, error);
          return { product, category: 'Other' };
        }
      });

      const batchResults = await Promise.allSettled(batchPromises);
      batchResults.forEach(result => {
        if (result.status === 'fulfilled') {
          const { product, category } = result.value;
          groups[category].push(product);
        }
      });
    }

    setGroupedProducts(groups);
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'SAT': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Solar': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'MTF': return 'text-green-600 bg-green-50 border-green-200';
      case 'MTB': return 'text-teal-600 bg-teal-50 border-teal-200';
      case 'Other': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'SAT': return '📡';
      case 'Solar': return '☀️';
      case 'MTF': return '🔧';
      case 'MTB': return '🛒';
      case 'Other': return '📦';
      default: return '📦';
    }
  };

  const getCategoryCount = (category) => {
    return groupedProducts[category]?.length || 0;
  };

  const getTotalProductCount = () => {
    return Object.values(groupedProducts).reduce((total, products) => total + products.length, 0);
  };

  const getFilteredCategories = () => {
    if (selectedCategory === 'all') {
      return Object.entries(groupedProducts).filter(([category, products]) => products.length > 0);
    } else {
      return Object.entries(groupedProducts).filter(([category, products]) => 
        category === selectedCategory && products.length > 0
      );
    }
  };

  const exportToExcel = () => {
    if (!data || !data.products) {
      alert('ไม่มีข้อมูลสำหรับ Export');
      return;
    }

    // Prepare data for Excel export
    const excelData = [];
    
    // Add header information
    excelData.push(['รายละเอียดการขายสินค้า']);
    excelData.push(['พนักงานขาย:', `${salesmanName} (${salesmanCode})`]);
    excelData.push(['ช่วงเวลา:', `${data.period?.start_date || startDate} ถึง ${data.period?.end_date || endDate}`]);
    excelData.push(['ทีม:', `${data.salesman_info?.team_name || ''} (${data.salesman_info?.team_id || ''})`]);
    excelData.push([]); // Empty row

    // Process products by category
    const categoriesToExport = selectedCategory === 'all' 
      ? getFilteredCategories() 
      : getFilteredCategories();

    categoriesToExport.forEach(([category, products]) => {
      // Category header
      excelData.push([`ประเภท: ${category} (${products.length} รายการ)`]);
      excelData.push([]); // Empty row

      products.forEach((product, productIndex) => {
        // Product header
        excelData.push(['ข้อมูลสินค้า']);
        excelData.push(['รหัสสินค้า:', product.item_number]);
        excelData.push(['ชื่อสินค้า:', product.item_description]);
        excelData.push(['หน่วย:', product.unit_of_measure?.trim()]);
        excelData.push(['จำนวนรวม:', salesTeamService.formatThaiInteger(product.total_quantity)]);
        excelData.push(['มูลค่ารวม:', `${salesTeamService.formatThaiNumber(product.total_amount)} บาท`]);
        excelData.push(['จำนวนใบกำกับ:', `${salesTeamService.formatThaiInteger(product.invoice_count)} ใบ`]);
        excelData.push(['ราคาเฉลี่ย:', `${salesTeamService.formatThaiNumber(product.average_price)} บาท`]);
        excelData.push([]); // Empty row

        // Invoice details header
        if (product.invoice_details && product.invoice_details.length > 0) {
          excelData.push(['รายละเอียดใบกำกับ']);
          excelData.push([
            'เลขที่ใบกำกับ',
            'วันที่',
            'รหัสลูกค้า',
            'จำนวน',
            'ราคาต่อหน่วย',
            'ยอดสุทธิ'
          ]);

          // Invoice data
          product.invoice_details.forEach(invoice => {
            excelData.push([
              invoice.inv_number?.trim(),
              formatDate(invoice.inv_date),
              invoice.customer_no,
              salesTeamService.formatThaiInteger(invoice.quantity),
              salesTeamService.formatThaiNumber(invoice.unit_price),
              salesTeamService.formatThaiNumber(invoice.net_amount)
            ]);
          });
        }

        excelData.push([]); // Empty row between products
        excelData.push([]); // Double empty row for separation
      });

      excelData.push([]); // Empty row between categories
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 20 }, // Column A
      { wch: 15 }, // Column B
      { wch: 15 }, // Column C
      { wch: 12 }, // Column D
      { wch: 15 }, // Column E
      { wch: 15 }  // Column F
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'รายละเอียดการขาย');

    // Generate filename
    const currentDate = formatDate(new Date()).replace(/\//g, '-');
    const filename = `รายละเอียดการขาย_${salesmanCode}_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">รายละเอียดการขาย</h2>
              <p className="text-blue-100 text-sm">
                {salesmanName} ({salesmanCode})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-600 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <Loader className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-red-800 mb-2">เกิดข้อผิดพลาด</h3>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={loadProductSalesDetail}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  ลองใหม่
                </button>
              </div>
            </div>
          ) : data ? (
            <div className="space-y-6">

              {/* Products List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    รายการสินค้าที่ขาย ({data.products?.length || 0} รายการ)
                  </h3>
                  
                  <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    disabled={!data.products || data.products.length === 0}
                  >
                    <Download className="w-4 h-4" />
                    Export Excel
                  </button>
                </div>

                {/* Category Filter Buttons */}
                {Object.keys(groupedProducts).some(category => groupedProducts[category].length > 0) && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Filter className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">กรองตามประเภท:</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {/* All Categories Button */}
                      <button
                        onClick={() => setSelectedCategory('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                          selectedCategory === 'all'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>🔍</span>
                        ทั้งหมด ({getTotalProductCount()})
                      </button>

                      {/* Individual Category Buttons */}
                      {Object.entries(groupedProducts).map(([category, products]) => 
                        products.length > 0 && (
                          <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                              selectedCategory === category
                                ? category === 'SAT' ? 'bg-blue-600 text-white'
                                : category === 'Solar' ? 'bg-yellow-600 text-white'
                                : category === 'MTF' ? 'bg-green-600 text-white'
                                : category === 'MTB' ? 'bg-teal-600 text-white'
                                : 'bg-gray-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <span>{getCategoryIcon(category)}</span>
                            {category} ({getCategoryCount(category)})
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}
                
                {Object.keys(groupedProducts).some(category => groupedProducts[category].length > 0) ? (
                  <div className="space-y-6">
                    {getFilteredCategories().map(([category, products]) => (
                      <div key={category} className="space-y-4">
                        {/* Category Header */}
                        <div className={`p-4 rounded-lg border-2 ${getCategoryColor(category)}`}>
                          <h4 className="text-lg font-semibold flex items-center gap-2">
                            <span className="text-xl">{getCategoryIcon(category)}</span>
                            {category} ({products.length} รายการ)
                          </h4>
                        </div>
                        
                        {/* Products in this category */}
                        <div className="space-y-4 ml-4">
                          {products.map((product, index) => (
                            <div key={`${category}-${index}`} className="border border-gray-200 rounded-lg overflow-hidden">
                              {/* Product Header */}
                              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">
                                      {product.item_description}
                                    </h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                      รหัสสินค้า: {product.item_number} | หน่วย: {product.unit_of_measure?.trim()}
                                    </p>
                                    
                                    {/* Product Summary Stats */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                      <div className="text-center">
                                        <p className="text-sm text-gray-500">จำนวนรวม</p>
                                        <p className="font-semibold text-blue-900">
                                          {salesTeamService.formatThaiInteger(product.total_quantity)}
                                        </p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-sm text-gray-500">มูลค่ารวม</p>
                                        <p className="font-semibold text-green-900">
                                          {salesTeamService.formatThaiNumber(product.total_amount)} บาท
                                        </p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-sm text-gray-500">ใบกำกับ</p>
                                        <p className="font-semibold text-purple-900">
                                          {salesTeamService.formatThaiInteger(product.invoice_count)} ใบ
                                        </p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-sm text-gray-500">ราคาเฉลี่ย</p>
                                        <p className="font-semibold text-orange-900">
                                          {salesTeamService.formatThaiNumber(product.average_price)} บาท
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Invoice Details */}
                              <div className="p-4">
                                <h5 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  รายละเอียดใบกำกับ ({product.invoice_details?.length || 0} รายการ)
                                </h5>
                                
                                {product.invoice_details && product.invoice_details.length > 0 ? (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                          <th className="text-left px-3 py-2 font-medium text-gray-700">เลขที่ใบกำกับ</th>
                                          <th className="text-left px-3 py-2 font-medium text-gray-700">วันที่</th>
                                          <th className="text-left px-3 py-2 font-medium text-gray-700">รหัสลูกค้า</th>
                                          <th className="text-right px-3 py-2 font-medium text-gray-700">จำนวน</th>
                                          <th className="text-right px-3 py-2 font-medium text-gray-700">ราคาต่อหน่วย</th>
                                          <th className="text-right px-3 py-2 font-medium text-gray-700">สุทธิ</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {product.invoice_details.map((invoice, invIndex) => (
                                          <tr key={invIndex} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-3 py-2 font-mono text-xs">
                                              {invoice.inv_number?.trim()}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                              {formatDate(invoice.inv_date)}
                                            </td>
                                            <td className="px-3 py-2 font-mono text-xs">
                                              {invoice.customer_no}
                                            </td>
                                            <td className="px-3 py-2 text-right font-semibold text-blue-900">
                                              {salesTeamService.formatThaiInteger(invoice.quantity)}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                              {salesTeamService.formatThaiNumber(invoice.unit_price)}
                                            </td>
                                            <td className="px-3 py-2 text-right font-semibold text-green-900">
                                              {salesTeamService.formatThaiNumber(invoice.net_amount)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <p className="text-gray-500 text-center py-4">ไม่มีรายละเอียดใบกำกับ</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : data.products && data.products.length > 0 ? (
                  <div className="text-center py-8">
                    <Loader className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">กำลังจัดกลุ่มสินค้า...</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">ไม่มีข้อมูลสินค้าที่ขาย</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">ไม่มีข้อมูลที่แสดง</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSalesDetailModal;